import { isServer } from '@bob/library'
import { CSCookiePolicy_CookieSubCategories } from '@websrc/Typings/ContentStack'

export type CookieSettingName = CSCookiePolicy_CookieSubCategories['category']

export interface CookiePreferences {
  key: CookieSettingName
  value: boolean
}

export type CookieConsent = { preferences: CookiePreferences[] }

const cookieName = 'LEGO_COOKIE_SETTINGS'
const expirationDays = 365

const listeners: { [key: string]: () => void } = {}

export const listenForCookie = (callback: () => void) => {
  const subId = Math.random().toString(36).substring(2, 8)

  listeners[subId] = callback

  return () => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete listeners[subId]
  }
}

export type SetCookie = {
  cookiePreferences: CookieConsent['preferences']
}

export const setCookie = ({ cookiePreferences }: SetCookie) => {
  const date = new Date()

  const cookieConsent: CookieConsent = {
    preferences: cookiePreferences,
  }

  date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000)

  const parsedValue = JSON.stringify(cookieConsent)

  const environment = process.env.CONTENTSTACK_ENVIRONMENT
  const buildType = process.env.BOB_BUILD_TYPE

  let cookieDomain = ''

  if (environment && environment === 'production' && buildType === 'dot-com') {
    cookieDomain = window.location.host.includes('lego.com') ? 'domain=.lego.com;' : ''
  }

  document.cookie = `${cookieName}=${parsedValue}; expires=${date.toUTCString()}; path=/; ${cookieDomain}`

  Object.values(listeners).forEach((listener) => listener())
}

export const clearCookie = (name?: string) => {
  document.cookie = `${name ?? cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
}

export const getCookieConsent = (timesCalled = 1): CookieConsent | null => {
  if (!isServer) {
    const cookie = document.cookie.match(`(^| )${cookieName}=([^;]+)`)

    if (cookie?.[2]) {
      try {
        const parsedCookie = JSON.parse(cookie?.[2]) as CookieConsent

        // temporary fix because our cookie changed, so we have set 2 with the same name
        // and if the browser reads the old one, the cookie popup will keep reappearing
        if ((parsedCookie as any).reconsentDate && timesCalled < 4) {
          clearCookie()

          // to be sure of not creating infinite loop
          return getCookieConsent(timesCalled + 1)
        }

        return parsedCookie
      } catch (e: unknown) {
        // if something fails to parse the cookie val always return necessary only
        const necessaryCookies: CookiePreferences[] = [
          { key: 'necessary', value: true },
          { key: 'analytics', value: false },
          { key: 'legomarketing', value: false },
          { key: 'thirdparty', value: false },
        ]

        setCookie({ cookiePreferences: necessaryCookies })

        return {
          preferences: necessaryCookies,
        }
      }
    }
  }

  return null
}

export const checkCookieConsent = (names?: CookieSettingName[], mode: 'any' | 'all' = 'all') => {
  const keys = names || ['necessary']
  const cookie = getCookieConsent()

  if (cookie?.preferences?.length) {
    try {
      return keys[mode === 'all' ? 'every' : 'some']((key) =>
        Boolean(cookie.preferences.find((pref) => pref.key === key && pref.value))
      )
    } catch (e: unknown) {
      return false
    }
  }

  return false
}
